/**
 * The AI connector consent page (`/connect`).
 *
 * The API's `/oauth/authorize` sends a person here with `?request=<id>`, or
 * with `?error=<code>` when it turned the request down before trusting the
 * app. The page shows who is asking, as whom, and what the company shares;
 * Allow or Deny answers with where the browser goes next.
 *
 * Security, as enforced here:
 *   - `frame-ancestors 'none'` is a response header, from the Pages Function
 *     and the Vite dev server; browsers ignore it in a meta tag.
 *   - Signed out → sign in first; the return stays on this origin
 *     (`resumeSession.ts`).
 *   - `clientName` is the app's own text: plain text, always beside its
 *     domain. A raw `?error=` value is never rendered.
 *   - The page never builds or edits `redirectUrl`; it only refuses one that
 *     is not http(s), or that leaves for a host other than the one shown.
 *   - Nothing is focused by default: Allow takes a deliberate press.
 *   - Allow answers whether the screen showed "can make changes": the value
 *     the person read, never a later copy. Not shown means read-only.
 *
 * No mocks: the entry app runs no MSW, so this page is walked against the
 * real backend.
 */
import { useEffect, useState, type JSX, type ReactNode } from "react";
import { AuthLayout, useAuthBranding } from "@linkhr/auth/pages";
import { useAuth } from "@linkhr/auth/react";
import {
  isApiError,
  useAnswerConnectorRequest,
  useConnectorRequest,
  type AuthorizationRequestView,
  type ConnectorAnswer,
} from "@linkhr/api-client";
import { Alert, Bidi, Button, Skeleton, nonEmpty } from "@linkhr/design-system";
import { useIntlLocale, useTranslation } from "@linkhr/i18n";

/** The shape of the API's request ids. Anything else never reaches the network. */
const REQUEST_ID = /^[A-Za-z0-9_-]{16,256}$/;

type Screen =
  | "invalid"
  | "busy"
  | "gone"
  | "otherBrowser"
  | "plan"
  | "disabled"
  | "notAllowed"
  | "failed";

/** First-strong isolate: a name keeps its own direction inside a sentence. */
const fsi = (s: string): string => `⁨${s}⁩`;
/** Left-to-right isolate: a host reads correctly inside Arabic text. */
const lri = (s: string): string => `⁦${s}⁩`;

/**
 * May the browser go to the API's `redirectUrl`? Only over http(s), and only to
 * the host the page showed, when it showed one ("Decline and go back" can run
 * before the request was ever read). `redirectHost` is the callback's host
 * without its port (Java `URI.getHost()`), so it is compared with `hostname`:
 * `host` carries the port that every loopback callback has.
 */
export function returnUrlAllowed(redirectUrl: string, shownHost: string | undefined): boolean {
  let url: URL;
  try {
    url = new URL(redirectUrl);
  } catch {
    return false;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  return shownHost === undefined || url.hostname === shownHost.toLowerCase();
}

function screenForError(error: unknown): Screen {
  if (!isApiError(error)) return "failed";
  // Unknown, expired or already answered; a server with connectors off 404s too.
  if (error.status === 404) return "gone";
  switch (error.errorCode) {
    case "MCP_CONSENT_OTHER_BROWSER":
      return "otherBrowser";
    case "PLAN_LOCKED":
      return "plan";
    case "MCP_CONNECTORS_DISABLED":
      return "disabled";
    case "MCP_NOT_ALLOWED":
      return "notAllowed";
    default:
      return "failed";
  }
}

function referenceOf(error: unknown): string | null {
  return isApiError(error) && error.correlationId !== "" ? error.correlationId : null;
}

export interface ConnectPageProps {
  request: string | undefined;
  error: string | undefined;
}

export function ConnectPage({ request, error }: ConnectPageProps): JSX.Element {
  const { t } = useTranslation("connect");
  const branding = useAuthBranding("", { passive: true });
  const { isAuthenticated } = useAuth();
  const id =
    error === undefined && request !== undefined && REQUEST_ID.test(request) ? request : null;

  const query = useConnectorRequest(id ?? "", { enabled: id !== null && isAuthenticated });
  const answer = useAnswerConnectorRequest();
  const view = query.data;

  // Read once: a session that ends later sends the browser to sign in by itself.
  const [signedOutOnArrival] = useState(!isAuthenticated);
  // The screen an answer led to. It holds while "Decline and go back" runs, so
  // the page never flashes back to the request in between.
  const [stuck, setStuck] = useState<Screen | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [expired, setExpired] = useState(false);
  const busy = answer.isPending || leaving;

  useEffect(() => {
    if (!signedOutOnArrival || id === null) return;
    // Replace, so Back does not land on a page that only sends you on.
    const back = window.location.pathname + window.location.search;
    window.location.replace(`/login?redirect=${encodeURIComponent(back)}`);
  }, [signedOutOnArrival, id]);

  // index.html names the tab for the sign-in pages; this one is not a sign-in.
  const tabTitle = t("connect:page.tab_title");
  useEffect(() => {
    const before = document.title;
    document.title = tabTitle;
    return () => {
      document.title = before;
    };
  }, [tabTitle]);

  const expiresAt = view?.expiresAt;
  useEffect(() => {
    if (expiresAt === undefined) return;
    // The device clock, so only a courtesy: the server answers 404 either way.
    const timer = window.setTimeout(() => setExpired(true), Date.parse(expiresAt) - Date.now());
    return () => window.clearTimeout(timer);
  }, [expiresAt]);

  const send = (choice: ConnectorAnswer, canChangeData?: boolean): void => {
    if (id === null) return;
    answer.mutate(
      { id, answer: choice, ...(canChangeData !== undefined ? { canChangeData } : {}) },
      {
        onSuccess: ({ redirectUrl }) => {
          if (!returnUrlAllowed(redirectUrl, view?.redirectHost)) {
            setStuck("failed");
            return;
          }
          setLeaving(true);
          window.location.assign(redirectUrl);
        },
        onError: (err) => setStuck(screenForError(err)),
      },
    );
  };

  const retry = (): void => {
    setStuck(null);
    answer.reset();
    void query.refetch();
  };

  const shown = ((): Screen | AuthorizationRequestView | null => {
    if (id === null) return error === "temporarily_unavailable" ? "busy" : "invalid";
    if (!isAuthenticated) return null; // on the way to the sign-in
    if (stuck !== null) return stuck;
    if (expired && !busy) return "gone";
    if (query.isError) return screenForError(query.error);
    return view ?? null;
  })();

  return (
    <AuthLayout branding={branding}>
      {shown === null ? (
        <Skeleton aria-label={nonEmpty(t("common:a11y.loading"))} className="h-64 rounded-lg" />
      ) : typeof shown === "string" ? (
        <StateScreen
          screen={shown}
          busy={busy}
          retrying={query.isFetching}
          correlationId={referenceOf(answer.error ?? query.error)}
          onDecline={() => send("deny")}
          onRetry={retry}
        />
      ) : (
        <RequestScreen view={shown} busy={busy} leaving={leaving} onAnswer={send} />
      )}
    </AuthLayout>
  );
}

function RequestScreen({
  view,
  busy,
  leaving,
  onAnswer,
}: {
  view: AuthorizationRequestView;
  busy: boolean;
  leaving: boolean;
  onAnswer: (choice: ConnectorAnswer, canChangeData?: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation("connect");
  const locale = useIntlLocale();
  const app = fsi(view.clientName);
  const title = t("connect:page.title", { clientName: app });
  const host = lri(view.redirectHost);
  const expires = new Intl.DateTimeFormat(locale, { timeStyle: "short" }).format(
    new Date(view.expiresAt),
  );

  return (
    <div className="flex flex-col gap-md">
      <header className="flex flex-col gap-xs">
        {/* The app picks its own name: one line here, all of it in the lead. */}
        <h1 className="text-heading truncate" title={title}>
          {title}
        </h1>
        <p className="text-body text-muted break-words">
          {t("connect:page.lead", {
            clientName: app,
            userName: fsi(view.userName),
            companyName: fsi(view.companyName),
          })}
        </p>
      </header>

      <dl className="flex flex-col gap-sm">
        <Row label={t("connect:page.field_app")}>
          <span className="block truncate font-semibold" title={view.clientName}>
            <Bidi dir="auto">{view.clientName}</Bidi>
          </span>
          <span className="text-caption text-muted block">
            {view.clientDomain ? (
              <Bidi dir="ltr">{view.clientDomain}</Bidi>
            ) : (
              t("connect:page.declared")
            )}
          </span>
        </Row>
        <Row label={t("connect:page.field_account")}>
          <Bidi dir="auto">{view.userName}</Bidi>
        </Row>
        <Row label={t("connect:page.field_company")}>
          <Bidi dir="auto">{view.companyName}</Bidi>
        </Row>
      </dl>

      <Alert variant="warning" description={t("connect:page.trust")} />
      {view.loopbackOnly ? (
        <Alert variant="warning" description={t("connect:page.loopback", { host })} />
      ) : (
        <p className="text-caption text-muted">{t("connect:page.returns_to", { host })}</p>
      )}

      <section aria-labelledby="connect-access" className="flex flex-col gap-xs">
        <h2 id="connect-access" className="text-body font-semibold">
          {t("connect:page.access_title")}
        </h2>
        <p className="text-body">
          {view.actsAs === "ADMIN"
            ? t("connect:page.acts_as_admin")
            : t("connect:page.acts_as_employee")}
        </p>
        <p className="text-body">
          {view.canChangeData ? t("connect:page.can_change") : t("connect:page.read_only")}
        </p>
        <ul className="text-body list-disc ps-md">
          <li>{view.sharePayFigures ? t("connect:page.pay_on") : t("connect:page.pay_off")}</li>
          <li>{view.shareIdentifiers ? t("connect:page.ids_on") : t("connect:page.ids_off")}</li>
        </ul>
        <p className="text-caption text-muted">{t("connect:page.company_sets")}</p>
      </section>

      <p className="text-caption text-muted">{t("connect:page.expires_at", { time: expires })}</p>

      {leaving ? (
        <p role="status" className="text-body">
          {t("connect:page.leaving", { clientName: app })}
        </p>
      ) : (
        <div className="flex flex-wrap justify-end gap-sm">
          <Button variant="secondary" disabled={busy} onClick={() => onAnswer("deny")}>
            {t("connect:page.deny")}
          </Button>
          {/* This render's view: what the person read is what Allow sends. */}
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => onAnswer("approve", view.canChangeData)}
          >
            {t("connect:page.allow")}
          </Button>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-label">{label}</dt>
      <dd className="text-body">{children}</dd>
    </div>
  );
}

function StateScreen({
  screen,
  busy,
  retrying,
  correlationId,
  onDecline,
  onRetry,
}: {
  screen: Screen;
  busy: boolean;
  retrying: boolean;
  correlationId: string | null;
  onDecline: () => void;
  onRetry: () => void;
}): JSX.Element {
  const { t } = useTranslation("connect");
  // Only where Deny still works: it checks the browser and the request, not
  // the company, so the app hears "denied" instead of waiting.
  const decline = (
    <Button variant="secondary" disabled={busy} onClick={onDecline} className="self-end">
      {t("connect:states.decline_return")}
    </Button>
  );
  switch (screen) {
    case "invalid":
      return <Message title={t("connect:states.invalid")} body={t("connect:states.start_again")} />;
    case "busy":
      return <Message title={t("connect:states.busy")} />;
    case "gone":
      return <Message title={t("connect:states.gone")} body={t("connect:states.start_again")} />;
    case "otherBrowser":
      return <Message title={t("connect:states.other_browser")} />;
    case "plan":
      return <Message title={t("connect:states.plan")}>{decline}</Message>;
    case "disabled":
      return <Message title={t("connect:states.disabled")}>{decline}</Message>;
    case "notAllowed":
      return <Message title={t("connect:states.not_allowed")}>{decline}</Message>;
    case "failed":
      return (
        <Message title={t("connect:states.failed")}>
          {correlationId !== null && (
            <p className="text-caption text-muted">
              {t("errors:boundary.reference", { correlationId: lri(correlationId) })}
            </p>
          )}
          <Button variant="primary" disabled={retrying} onClick={onRetry} className="self-end">
            {t("connect:states.retry")}
          </Button>
        </Message>
      );
  }
}

function Message({
  title,
  body,
  children,
}: {
  title: string;
  body?: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    // An alert: it often replaces the button that was just pressed.
    <div role="alert" className="flex flex-col gap-md">
      <h1 className="text-heading">{title}</h1>
      {body !== undefined && <p className="text-body text-muted">{body}</p>}
      {children}
    </div>
  );
}
